import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { isDemoMode, getRuntimeMode } from '../lib/runtime-mode.js';
// Session-level demo check. NOT the same as runtime-mode's isDemoMode(), which
// reports the BUILD mode and is false in production — see MfaEnrollBanner.
import { isDemoMode as inDemoSession } from '../lib/demo-customer.js';
import { getAccountNumber, isAuthenticated } from '../lib/auth.js';
import { getTotpStatus } from '../lib/totp-enroll.js';

/**
 * RuntimeBanners — two persistent banners:
 *   1. Tenant-mode chip (Demo / Evaluation / Production) — three tones
 *   2. "New version available — refresh" banner when SW detects an update
 *
 * Demo-mode UX subtlety (added 2026-05-29 per CTO audit):
 * The amber "DEMO MODE" banner reads "you don't have a real backend"
 * to a Fortune 100 evaluator. We now distinguish:
 *   - Demo account (explicit DEMO-CTO / DEMO-AUDIT)  → amber + loud
 *   - Evaluation tenant (any other account on mock)  → slate + quiet
 *   - Production (real Cognito + API + Stripe)       → no banner
 * Same underlying mock data, very different messaging.
 */
export default function RuntimeBanners() {
  return (
    <>
      <TenantBanner />
      <MfaEnrollBanner />
      <UpdateBanner />
    </>
  );
}

/**
 * MfaEnrollBanner — nudges a signed-in real user who somehow holds a session
 * without an enrolled authenticator toward /security.
 *
 * Copy corrected 2026-09-30. It used to read "Two-factor auth required soon —
 * Starting {MFA_REQUIRED_DATE}…", which went stale the moment that date passed
 * (2026-08-26) and the pool flipped to Mfa.REQUIRED. It spent five weeks
 * announcing a future that had already happened, on a page prospects can reach.
 *
 * Two demo checks, deliberately. runtime-mode's isDemoMode() reports the BUILD
 * mode and is FALSE in production, so it never suppressed anything on the live
 * site — which is how this banner ended up displayed over the public demo, the
 * one audience that must not see an internal compliance nudge. inDemoSession()
 * checks the actual session and is the one that does the work here.
 *
 * With MFA required, a session normally cannot exist without an enrolled
 * factor, so this should be close to unreachable for real users. It is kept
 * rather than deleted because the server-minted demo and any pre-cutover
 * session are exactly the cases that reach it.
 */
function MfaEnrollBanner() {
  const [hidden, setHidden] = useState(() => {
    try { return sessionStorage.getItem('netcom_connect_mfa_banner_hidden') === '1'; } catch { return false; }
  });
  const [enrolled, setEnrolled] = useState(true); // default hidden until we know otherwise

  useEffect(() => {
    if (!getRuntimeMode().realAuth || isDemoMode() || inDemoSession() || !isAuthenticated()) return;
    let alive = true;
    getTotpStatus()
      .then((s) => { if (alive) setEnrolled(!!s.enrolled); })
      .catch(() => { /* fail closed — don't nag if we can't tell */ });
    return () => { alive = false; };
  }, []);

  if (!getRuntimeMode().realAuth || isDemoMode() || inDemoSession() || !isAuthenticated() || enrolled || hidden) return null;

  function hide() {
    setHidden(true);
    try { sessionStorage.setItem('netcom_connect_mfa_banner_hidden', '1'); } catch { /* ignore */ }
  }

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between gap-3 text-xs">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span className="text-amber-700 font-semibold flex-shrink-0">🔐 Two-factor authentication required</span>
        <span className="text-amber-800 truncate">
          Every sign-in needs a code from your authenticator app.{' '}
          <Link to="/security" className="underline font-medium">Set it up now</Link> so you're not locked out.
        </span>
      </div>
      <button onClick={hide} aria-label="Hide MFA reminder" className="text-amber-700 hover:text-amber-900 flex-shrink-0 p-1 -m-1">
        <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="m4 4 8 8M12 4l-8 8" strokeLinecap="round" /></svg>
      </button>
    </div>
  );
}

function TenantBanner() {
  const [hidden, setHidden] = useState(() => {
    try { return sessionStorage.getItem('netcom_connect_tenant_banner_hidden') === '1'; } catch { return false; }
  });

  if (!isDemoMode() || hidden) return null;

  const accountNumber = getAccountNumber() || '';
  const isExplicitDemo =
    accountNumber.includes('DEMO-CTO') ||
    accountNumber.includes('DEMO-AUDIT') ||
    accountNumber === 'REPLACE_ME';

  function hide() {
    setHidden(true);
    try { sessionStorage.setItem('netcom_connect_tenant_banner_hidden', '1'); } catch { /* ignore */ }
  }

  // Demo account — amber, loud, explicit about the mock data plumbing.
  // Internal team + scripted product walkthroughs see this.
  if (isExplicitDemo) {
    return (
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="text-amber-700 font-semibold flex-shrink-0">DEMO ACCOUNT</span>
          <span className="text-amber-800 truncate">
            Synthesized fleet data · MFA accepts any 6 digits · No real charges · Real wiring activates when Andrew completes account provisioning.
          </span>
        </div>
        <button onClick={hide} aria-label="Hide demo banner" className="text-amber-700 hover:text-amber-900 flex-shrink-0 p-1 -m-1">
          <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="m4 4 8 8M12 4l-8 8" strokeLinecap="round" /></svg>
        </button>
      </div>
    );
  }

  // Evaluation tenant — slate, quiet, frames as a real product trial.
  // External evaluators landing on the live URL see this; no "DEMO"
  // word anywhere, no "mock data" phrase. Same data underneath, very
  // different read.
  return (
    <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 flex items-center justify-between gap-3 text-xs">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span className="text-slate-700 font-semibold flex-shrink-0">EVALUATION TENANT</span>
        <span className="text-slate-600 truncate">
          You're using a sandboxed account preloaded with representative fleet data so you can explore the full operations surface.
        </span>
      </div>
      <button onClick={hide} aria-label="Hide banner" className="text-slate-500 hover:text-slate-800 flex-shrink-0 p-1 -m-1">
        <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="m4 4 8 8M12 4l-8 8" strokeLinecap="round" /></svg>
      </button>
    </div>
  );
}

function UpdateBanner() {
  const [show, setShow] = useState(false);
  const [reg, setReg] = useState(null);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    function detect(reg) {
      if (!reg) return;
      setReg(reg);
      if (reg.waiting) {
        setShow(true);
      }
      reg.addEventListener('updatefound', () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener('statechange', () => {
          if (nw.state === 'installed' && navigator.serviceWorker.controller) {
            setShow(true);
          }
        });
      });
    }
    navigator.serviceWorker.getRegistration().then((reg) => reg && detect(reg));
  }, []);

  function refresh() {
    if (reg?.waiting) reg.waiting.postMessage({ type: 'SKIP_WAITING' });
    setTimeout(() => window.location.reload(), 200);
  }

  if (!show) return null;
  return (
    <div className="bg-[var(--color-brand-50)] border-b border-[var(--color-brand-200)] px-4 py-2 flex items-center justify-between gap-3 text-xs">
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-[var(--color-brand-700)] font-semibold">✨ New version available</span>
        <span className="text-[var(--color-brand-700)] hidden sm:inline">Refresh to update the portal.</span>
      </div>
      <button onClick={refresh} className="bg-[var(--color-brand-500)] hover:bg-[var(--color-brand-600)] text-white font-semibold px-3 py-1 rounded text-xs">
        Refresh
      </button>
    </div>
  );
}
